import React, { useCallback, useContext, useEffect, useState } from 'react';
import SideMenu from '../../Splitter/SideMenu/SideMenu';
import HeaderMinimal from '../Headers/HeaderMinimal';
import {
  ClientContext,
  ExpertContext,
  ProfileContext,
} from '../../Contexts/contexts';
import ReactSelect from 'react-select';
import useAuth from '../../Contexts/useAuth';
import Services from '../../Services/services';
import DeleteAccountConfirmationModal from './DeleteAccountConfirmationModal/DeleteAccountConfirmationModal';
import { dayJS } from '../../../utils/date';
import {
  numberKeyPress,
  numberValueChanged,
} from '../../../utils/InputVerifications';
import PaymentElements from './PaymentMethods/PaymentElements';
import useToast from '../../Toasts/useToast';
import {
  ToastHeaders,
  ToastThemes,
  ToastMessages,
} from '../../Toasts/ToastOptions/toastOptions';

export default function Settings() {
  const profileContext = useContext(ProfileContext);
  const expertContext = useContext(ExpertContext);
  const clientContext = useContext(ClientContext);

  const { checkAuth } = useAuth();

  const [email, setEmail] = useState('');

  const [newPassword, setNewPassword] = useState({
    value: '',
    verification: '',
  });

  const [periodRequest, setPeriodRequest] = useState(undefined);

  const [selectedPeriod, setSelectedPeriod] = useState(undefined);

  const [nbNewClients, setNbNewClients] = useState(0);

  const [activAccount, setActivAccount] = useState(
    expertContext?.expert?.isOpen
  );

  const [showDeleteModal, setShowModalDelete] = useState(false);

  const [phone, setPhone] = useState('');

  const periodOptions = [
    { value: null, label: 'Sélectionner une période' },
    { value: 'mensuel', label: 'mensuel' },
    { value: 'trimestriel', label: 'trimestriel' },
    { value: 'semestriel', label: 'semestriel' },
    { value: 'annuel', label: 'annuel' },
    { value: 'immediat', label: 'immediat' },
  ];

  const enqueueToast = useToast();

  const fetchPaymentPeriodRequest = useCallback(async () => {
    if (expertContext?.expert?.paymentPeriod === undefined) return;

    try {
      const paymentPeriodRequests =
        await Services.requestService.paymentPeriodRequestByExpertID(
          expertContext?.expertId
        );

      if (paymentPeriodRequests.length) {
        setPeriodRequest(paymentPeriodRequests[0]);
      }
    } catch (error) {
      console.error(error);
    }
  }, [expertContext?.expert?.paymentPeriod, expertContext?.expertId]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    fetchPaymentPeriodRequest();
  }, [fetchPaymentPeriodRequest]);

  useEffect(() => {
    if (expertContext?.expert !== undefined && selectedPeriod === undefined) {
      if (expertContext?.expert?.paymentPeriod === null)
        setSelectedPeriod({ value: null, label: 'Sélectionner une période' });
      else
        setSelectedPeriod({
          value: expertContext?.expert?.paymentPeriod,
          label: expertContext?.expert?.paymentPeriod,
        });
    }
  }, [expertContext?.expert, selectedPeriod]);

  useEffect(() => {
    setNbNewClients(expertContext?.expert?.nbNewClients || 0);
  }, [expertContext?.expert]);

  function updatePassword(e, key) {
    const { value } = e.target;

    setNewPassword((prevState) => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  }

  async function changeEmail() {
    if (!email.length) return;

    const emailValidationRegex = new RegExp(
      /* eslint-disable */
      /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g
    );

    if (emailValidationRegex.test(email)) {
      // Update email here
      try {
        await Services.userService.updateUserByID(profileContext?.user?.id, {
          email,
        });
        if (profileContext?.isClient) {
          await Services.profileService.updateClientByID(
            clientContext?.clientId,
            {
              email,
            }
          );
        }
        setEmail('');
        profileContext?.shouldFetchUser();
      } catch (error) {
        console.error(error);
      }
    }
  }

  async function changePassword() {
    if (!newPassword.value.length || !newPassword.verification.length) return;

    if (newPassword.value !== newPassword.verification) return;

    // Update user here
    try {
      await Services.userService.updateUserByID(profileContext?.user?.id, {
        password: newPassword.value,
      });
      profileContext?.logout();
    } catch (error) {
      console.error(error);
    }
  }

  async function updatePhone() {
    if (!phone.length) return;

    const re = /^[+][0-9]{2,3}[0-9]{9}/;
    const input = document.getElementById('update-phone');

    if (re.test(phone)) {
      // Update phone here
      try {
        await Services.userService.updateUserByID(profileContext?.user?.id, {
          phone,
        });
        if (profileContext?.isClient) {
          await Services.profileService.updateClientByID(
            clientContext?.clientId,
            {
              phone,
            }
          );
        }
        input?.setCustomValidity('');
        setPhone('');
        profileContext?.shouldFetchUser();
      } catch (error) {
        console.error(error);
      }
    } else {
      input?.setCustomValidity(
        `Le numéro de téléphone doit avoir le format +33XXXXXXXXX`
      );
      input?.reportValidity();
    }
  }

  async function changePeriod() {
    if (selectedPeriod === undefined) return;

    if (selectedPeriod.value === expertContext?.expert?.paymentPeriod) return;

    if (periodRequest !== undefined) return;

    if (selectedPeriod.value === null) return;

    if (expertContext?.expert?.paymentPeriod === null) {
      try {
        await Services.profileService.updateExpertByID(
          expertContext?.expert?.id,
          { id: expertContext?.expert?.id, paymentPeriod: selectedPeriod.value }
        );

        await Services.profileService.createExpertStripeCustomer(
          expertContext?.expert?.id
        );

        await Services.profileService.updateExpertByID(
          expertContext?.expert?.id,
          {
            id: expertContext?.expert?.id,
            isOpen: true,
          }
        );

        setActivAccount(true);
        profileContext?.shouldFetchExpert();

        enqueueToast(ToastMessages.CONFIRM_SUCCESS, {
          theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
          header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
          duration: 3000,
        });
      } catch (error) {
        console.error(error);
        enqueueToast(
          'Veuillez contacter le service client si cela ce reproduit',
          {
            theme: ToastThemes.toastThemes.ERROR_OCCURED,
            header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
            duration: 5000,
          }
        );
      }
    } else {
      // Update expert here
      try {
        const paymentPeriodRequest =
          await Services.requestService.createPaymentPeriodRequest(
            expertContext?.expertId,
            expertContext?.expert?.paymentPeriod,
            selectedPeriod.value
          );

        setPeriodRequest(paymentPeriodRequest);

        enqueueToast(ToastMessages.CONFIRM_SUCCESS_APPOINTMENT, {
          theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
          header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
          duration: 3000,
        });
      } catch (error) {
        console.error(error);
        enqueueToast(
          'Veuillez contacter le service client si cela ce reproduit',
          {
            theme: ToastThemes.toastThemes.ERROR_OCCURED,
            header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
            duration: 5000,
          }
        );
      }
    }
  }

  async function changeClientNumber() {
    if (nbNewClients < 0) return;

    // Update expert here
    try {
      await Services.profileService.updateExpertByID(expertContext?.expertId, {
        id: expertContext?.expert?.id,
        nbNewClients,
      });

      profileContext?.shouldFetchExpert();
    } catch (error) {
      console.error(error);
    }
  }

  async function UpdateOpenState(isOpen) {
    if (expertContext?.expert?.id) {
      await Services.profileService.updateExpertByID(expertContext?.expertId, {
        id: expertContext?.expert?.id,
        isOpen,
      });

      profileContext?.shouldFetchExpert();
    }
  }

  async function ConfirmDelete() {
    try {
      await Services.userService.updateUserByID(profileContext?.user?.id, {
        deletedAt: dayJS().toISOString(),
      });

      profileContext?.logout();
    } catch (error) {
      console.error(error);
    }
  }

  const handleValueChanged = (e) => {
    numberValueChanged(e, null, (value) => setNbNewClients(value));
  };

  const handleKeyPress = (e) => {
    numberKeyPress(e, null);
  };

  const handleChangedPhone = (e) => {
    e.target.setCustomValidity('');

    setPhone(e.target.value);
  };

  return (
    <>
      {!profileContext.fetchingUser && profileContext.role !== undefined && (
        <>
          <HeaderMinimal />
          <div className="container-dashboard settings-main-container">
            <SideMenu />
            <form className="container-settings" autoComplete="off">
              <div className="settings-groups">
                <label className="label-settings" htmlFor="update-email">
                  Changer mon adresse email:
                </label>
                <div className="settings-input-groups">
                  <input
                    className="input-settings"
                    type="email"
                    id="update-email"
                    name="update-email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Taper votre nouvelle adresse email"
                  />
                  <button
                    className="update-button"
                    type="button"
                    onClick={() => changeEmail()}
                  >
                    Cliquer ici pour modifier
                  </button>
                </div>
              </div>
              <div className="settings-groups">
                <label className="label-settings" htmlFor="update-password">
                  Changer mon mot de passe:
                </label>
                <div className="settings-input-groups">
                  <div className="settings-input-passwords">
                    <input
                      className="input-settings upper-input"
                      type="password"
                      id="update-password"
                      name="update-password"
                      autoComplete="new-password"
                      value={newPassword.value}
                      onChange={(e) => updatePassword(e, 'value')}
                      placeholder="Taper votre nouveau mot de passe"
                    />
                    <input
                      className="input-settings"
                      type="password"
                      id="new-password"
                      name="new-password"
                      autoComplete="new-password"
                      value={newPassword.verification}
                      onChange={(e) => updatePassword(e, 'verification')}
                      placeholder="Confirmer votre nouveau mot de passe"
                    />
                  </div>

                  <button
                    className="update-button"
                    type="button"
                    onClick={() => changePassword()}
                  >
                    Cliquer ici pour modifier
                  </button>
                </div>
              </div>
              <div className="settings-groups">
                <label className="label-settings" htmlFor="update-phone">
                  Changer mon numéro de téléphone:
                </label>
                <div className="settings-input-groups">
                  <input
                    className="input-settings"
                    id="update-phone"
                    name="update-email"
                    autoComplete="off"
                    value={phone}
                    onChange={handleChangedPhone}
                    placeholder="Ex: +33612345678"
                  />
                  <button
                    className="update-button"
                    type="button"
                    onClick={() => updatePhone()}
                  >
                    Cliquer ici pour modifier
                  </button>
                </div>
              </div>
              {profileContext.isExpert && (
                <>
                  {periodRequest === undefined && (
                    <div className="settings-groups">
                      <label
                        className="label-settings"
                        htmlFor="update-periodicity"
                      >
                        Choix de la periodicité de paiement des commissions:
                      </label>
                      <div className="settings-input-groups">
                        <ReactSelect
                          options={periodOptions}
                          value={selectedPeriod}
                          onChange={(period) => setSelectedPeriod(period)}
                          className="period-select"
                          classNamePrefix="period-select"
                          isSearchable={false}
                        />
                        <button
                          className="update-button"
                          type="button"
                          onClick={() => changePeriod()}
                        >
                          Cliquer ici pour valider votre choix
                        </button>
                      </div>
                      {expertContext?.expert?.paymentPeriod === null && (
                        <span className="settings-red-message">
                          Vous devez choisir une périodicité de paiement pour
                          pouvoir naviguer sur la plateforme. Merci de votre
                          compréhension. Ce message disparaitra quand vous aurez
                          validé votre périodicité de paiement.
                        </span>
                      )}
                    </div>
                  )}
                  {periodRequest !== undefined && (
                    <div className="settings-groups">
                      <label
                        className="label-settings"
                        htmlFor="update-periodicity"
                      >
                        Choix de la periodicité de paiement des commissions:
                      </label>
                      <div className="settings-input-groups">
                        <span className="period-request-feedback">
                          Votre demande est en cours de traitement, si elle est
                          évaluée positivement, la périodicité s'appliquera aux
                          prochains clients.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="settings-groups">
                    <label
                      className="label-settings"
                      htmlFor="update-nb-clients"
                    >
                      Nombre de clients par année:
                    </label>
                    <div className="settings-input-groups">
                      <input
                        className="input-settings"
                        type="number"
                        id="update-nb-clients"
                        name="update-nb-clients"
                        min={0}
                        value={nbNewClients}
                        onChange={handleValueChanged}
                        onKeyDown={handleKeyPress}
                      />
                      <button
                        className="update-button"
                        type="button"
                        onClick={() => changeClientNumber()}
                      >
                        Cliquer ici pour modifier
                      </button>
                    </div>
                  </div>
                  {expertContext?.expert?.paymentPeriod !== null && (
                    <div className="settings-groups">
                      <label className="activity-account">
                        Compte actif (si le bouton est à droite):
                      </label>
                      <label className="switch">
                        <input
                          className="hidden-checkbox"
                          type="checkbox"
                          checked={activAccount}
                          onChange={() => {
                            setActivAccount(!activAccount);
                            UpdateOpenState(!activAccount);
                          }}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  )}
                  <div className="settings-groups">
                    <label className="activity-account">
                      Sauvegarder mon moyen de paiement favori:
                    </label>
                    <PaymentElements expert={expertContext?.expert} />
                  </div>
                </>
              )}
              <button
                type="button"
                className="settings-delete-account-btn"
                onClick={() => setShowModalDelete(true)}
              >
                Supprimer le compte
              </button>
            </form>
          </div>
          {showDeleteModal && (
            <DeleteAccountConfirmationModal
              hideModal={() => setShowModalDelete(false)}
              confirmDelete={() => ConfirmDelete()}
            />
          )}
        </>
      )}
    </>
  );
}
